import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CategoriesAPI from '@/api/CategoriesAPI';
import { showErrorToast, showSuccessToast } from '@/components';
import { ISeoFilterGroup, useCategorySeoFilters } from './useCategorySeoFilters';

// Map of filterId -> selected filter value ids (the SEO landing selection).
export type TSeoSelected = Record<string, string[]>;

export interface IUseSeoFilterEditor {
  groups: ISeoFilterGroup[];
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  selected: TSeoSelected;
  totalSelected: number;
  totalValues: number;
  toggleValue: (filterId: string, valueId: string, checked: boolean) => void;
  toggleAll: (filterId: string, valueIds: string[]) => void;
  save: () => Promise<boolean>;
}

// Stable signature of a selection, so we can compare current vs saved regardless
// of key/value ordering.
const signature = (selected: TSeoSelected): string =>
  Object.keys(selected)
    .sort()
    .map((filterId) => `${filterId}:${[...selected[filterId]].sort().join(',')}`)
    .join('|');

// Owns the SEO landing-value selection for a category: loads the persisted state,
// tracks dirty vs saved, and persists on save. Kept outside the view so the page
// container can guard category switches against unsaved changes.
export const useSeoFilterEditor = (categoryId: string | null): IUseSeoFilterEditor => {
  const { t } = useTranslation(['seo']);
  const { groups, isLoading } = useCategorySeoFilters(categoryId);

  const [selected, setSelected] = useState<TSeoSelected>({});
  const [savedSignature, setSavedSignature] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initialise from the persisted seo_filters whenever a category's filters
  // arrive, and treat that snapshot as the saved baseline.
  useEffect(() => {
    const initial: TSeoSelected = {};
    groups.forEach((group) => {
      initial[group.filter.id] = group.seoValueIds;
    });
    setSelected(initial);
    setSavedSignature(signature(initial));
  }, [groups]);

  const { totalSelected, totalValues } = useMemo(() => {
    const totalValuesCount = groups.reduce((acc, group) => acc + group.values.length, 0);
    const totalSelectedCount = groups.reduce((acc, group) => acc + (selected[group.filter.id]?.length ?? 0), 0);
    return { totalSelected: totalSelectedCount, totalValues: totalValuesCount };
  }, [groups, selected]);

  const isDirty = useMemo(() => signature(selected) !== savedSignature, [selected, savedSignature]);

  const toggleValue = (filterId: string, valueId: string, checked: boolean) => {
    setSelected((prev) => {
      const current = prev[filterId] ?? [];
      return {
        ...prev,
        [filterId]: checked ? [...current, valueId] : current.filter((id) => id !== valueId),
      };
    });
  };

  const toggleAll = (filterId: string, valueIds: string[]) => {
    setSelected((prev) => {
      const allSelected = (prev[filterId]?.length ?? 0) === valueIds.length;
      return { ...prev, [filterId]: allSelected ? [] : valueIds };
    });
  };

  const save = async (): Promise<boolean> => {
    if (!categoryId) return false;

    setIsSaving(true);
    try {
      await CategoriesAPI.updateCategorySeoFilters(categoryId, {
        filters: groups.map((group) => ({
          filter_id: group.filter.id,
          value_ids: selected[group.filter.id] ?? [],
        })),
      });
      setSavedSignature(signature(selected));
      showSuccessToast({ message: t('seo_filters_saved') });
      return true;
    } catch {
      showErrorToast({ message: t('seo_filters_save_error') });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    groups,
    isLoading,
    isSaving,
    isDirty,
    selected,
    totalSelected,
    totalValues,
    toggleValue,
    toggleAll,
    save,
  };
};
